import {Injectable} from '@angular/core';
import {AddressTypeEnum, StructureAddress} from '@docma-mm-core/address/structure-address';
import { Structure } from '@docma-mm-core/structure/structure';
import {MmTranslateService} from '../../mm-translate.service';


// Regular Expressions for parsing tags and attributes
const SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
// ! to ~ is the ASCII range.
const NON_ALPHANUMERIC_REGEXP = /([^#-~ |!])/g;

function encodeEntities(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(SURROGATE_PAIR_REGEXP, (match: string) => {
      const hi = match.charCodeAt(0);
      const low = match.charCodeAt(1);
      return `&#${(hi - 0xd800) * 0x400 + (low - 0xdc00) + 0x10000};`;
    })
    .replace(NON_ALPHANUMERIC_REGEXP, (match: string) => `&#${match.charCodeAt(0)};`)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}


export interface StructureFilterDisplayState {
  structure: Structure & { innerHtml?: string };

  filterTerms: string[];

  matchedFieldGroups: Array<{
    addressType: AddressTypeEnum;
    field: string;
    values: string[];
  }>;
}

@Injectable()
export class MmStructureFilterService {

  private UNIQUE_WRAPPERS: [string, string] = ['##==-open_tag-==##', '##==-close_tag-==##'];

  public ADDRESS_FIELD_ORDER = [
    'DisplayName',
    'Name1',
    'FirstName',
    'Name2',
    'Salutation',
    'Street',
    'City',
    'Phone',
    'Mobile',
    'Fax',
    'Email',
    'Text1',
    'Text2',
    'User.Account',
    'Id',
    'Uid'
  ];

  public readonly innerHtmlByStructureId = new Map<number, string>();

  public readonly filterDisplayStates = new Map<number, StructureFilterDisplayState>();

  public _showIdColumns = false;

  public set showIdColumns(value: boolean) {
    this._showIdColumns = value;
  }

  constructor(private readonly translateService: MmTranslateService) {
  }

  public setInnerHtml(structure: Structure, innerHtml: string): void {
    this.innerHtmlByStructureId.set(
      structure.Id,
      innerHtml
    );
  }

  public getInnerHtml(structure: Structure): string {
    return (
      this.innerHtmlByStructureId.get(
        structure.Id
      ) ??
      this.renderStructureName(
        structure,
        []
      )
    );
  }

  public clearInnerHtml(structure: Structure): void {
    this.innerHtmlByStructureId.delete(
      structure.Id
    );
  }

  public normalizeFilterTerms(
    filters: string[]
  ): string[] {

    const result: string[] = [];
    const seen = new Set<string>();

    for (const filter of filters ?? []) {
      const value = filter?.trim();

      if (!value) {
        continue;
      }

      const normalized =
        value.toLocaleLowerCase();

      if (seen.has(normalized)) {
        continue;
      }

      seen.add(normalized);
      result.push(value);
    }

    return result;
  }


  public containsIgnoreCase(
    value: unknown,
    filter: string
  ): boolean {

    if (
      value === null ||
      value === undefined
    ) {
      return false;
    }

    return String(value)
      .toLocaleLowerCase()
      .includes(
        filter.toLocaleLowerCase()
      );
  }

  public getSearchableAddressFields(
    structureAddresses: StructureAddress[]
  ): Array<{
    structureAddress: StructureAddress;
    addressType: AddressTypeEnum;
    field: string;
    value: string;
  }> {

    const result: Array<{
      structureAddress: StructureAddress;
      addressType: AddressTypeEnum;
      field: string;
      value: string;
    }> = [];

    for (const structureAddress of structureAddresses) {

      const address = structureAddress?.Address;

      if (!address) {
        continue;
      }

      const addressType = structureAddress.AddressType;

      const processedFields = new Set<string>();

      /*
       * Add known fields in the required priority.
       */
      for (const field of this.ADDRESS_FIELD_ORDER) {

        /*
         * Id / Uid must never participate in filtering
         * when the corresponding columns are hidden.
         *
         * Add them to processedFields even when skipped,
         * otherwise the fallback Object.entries() loop
         * could add them again.
         */
        processedFields.add(field);

        if (
          this.isIdField(field) &&
          !this._showIdColumns
        ) {
          continue;
        }

        let value: unknown;

        if (field === 'User.Account') {
          value = address.User?.Account;
        } else {
          value = (address as any)[field];
        }

        if (!this.isSearchableAddressValue(value)) {
          continue;
        }

        result.push({
          structureAddress,
          addressType,
          field,
          value: this.addressValueToString(value)
        });
      }

      /*
       * Preserve the old behavior for any other scalar Address fields
       * that are not part of the explicit priority list.
       *
       * They have lower priority than all fields above.
       */
      for (const [field, value] of Object.entries(address)) {

        if (
          processedFields.has(field) ||
          field === 'User' ||
          (
            this.isIdField(field) &&
            !this._showIdColumns
          ) ||
          !this.isSearchableAddressValue(value)
        ) {
          continue;
        }

        result.push({
          structureAddress,
          addressType,
          field,
          value: this.addressValueToString(value)
        });
      }
    }

    return result;
  }

  public getMatchedAddressFieldGroups(
    addressFields: Array<{
      structureAddress: StructureAddress;
      addressType: AddressTypeEnum;
      field: string;
      value: string;
    }>,
    filters: string[]
  ): Array<{
    addressType: AddressTypeEnum;
    field: string;
    values: string[];
  }> {

    /*
     * Step 1:
     *
     * Select exactly ONE matching field per StructureAddress.
     *
     * addressFields is already ordered by ADDRESS_FIELD_ORDER,
     * therefore the first matching field wins.
     */
    const selectedFields =
      new Map<
        StructureAddress,
        {
          structureAddress: StructureAddress;
          addressType: AddressTypeEnum;
          field: string;
          value: string;
        }
      >();

    for (const addressField of addressFields) {

      /*
       * This StructureAddress already has its highest-priority match.
       */
      if (
        selectedFields.has(
          addressField.structureAddress
        )
      ) {
        continue;
      }

      const matches = filters.some(filter =>
        this.containsIgnoreCase(
          addressField.value,
          filter
        )
      );

      if (!matches) {
        continue;
      }

      selectedFields.set(
        addressField.structureAddress,
        addressField
      );
    }

    /*
     * Step 2:
     *
     * Keep existing grouping semantics:
     *
     * AddressType + field
     *
     * Multiple StructureAddress items can therefore still collapse
     * into one result.
     */
    const groups = new Map<
      string,
      {
        addressType: AddressTypeEnum;
        field: string;
        values: string[];
        normalizedValues: Set<string>;
      }
    >();

    for (
      const addressField
      of selectedFields.values()
      ) {

      const key =
        `${addressField.addressType}` +
        `::${addressField.field}`;

      let group = groups.get(key);

      if (!group) {
        group = {
          addressType:
          addressField.addressType,

          field:
          addressField.field,

          values: [],

          normalizedValues:
            new Set<string>()
        };

        groups.set(key, group);
      }

      const normalizedValue =
        addressField.value
          .toLocaleLowerCase();

      if (
        group.normalizedValues.has(
          normalizedValue
        )
      ) {
        continue;
      }

      group.normalizedValues.add(
        normalizedValue
      );

      group.values.push(
        addressField.value
      );
    }

    return Array
      .from(groups.values())
      .map(group => ({
        addressType:
        group.addressType,

        field:
        group.field,

        values:
        group.values
      }));
  }

  private isSearchableAddressValue(
    value: unknown
  ): boolean {

    if (
      value === null ||
      value === undefined
    ) {
      return false;
    }

    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value instanceof Date
    );
  }


  private addressValueToString(
    value: unknown
  ): string {

    if (value instanceof Date) {
      return value.toLocaleDateString();
    }

    return String(value);
  }

  public renderStructureName(
    structure: Structure,
    filters: string[]
  ): string {

    const name1 =
      this.highlightValues(
        structure.Name1 ?? '',
        filters
      );

    const name2 =
      this.highlightValues(
        structure.Name2 ?? '',
        filters
      );

    if (!structure.Name1) {
      return name2;
    }

    if (!structure.Name2) {
      return name1;
    }

    return `${name1} - ${name2}`;
  }

  private highlightValues(
    value: unknown,
    highlightValues: string[]
  ): string {

    const stringValue =
      String(value ?? '');

    const values = (
      highlightValues ?? []
    )
      .map(value => value?.trim())
      .filter(
        value =>
          value !== null &&
          value !== undefined &&
          value.length > 0
      );

    if (values.length === 0) {
      return encodeEntities(stringValue);
    }

    /*
     * Longest value first.
     *
     * For example:
     *
     * "Berlin Mitte"
     * "Berlin"
     *
     * should prefer the longer match.
     */
    values.sort(
      (a, b) =>
        b.length - a.length
    );

    const searchValues = values
      .map(highlightValue =>
        highlightValue.replace(
          /([.*+?^=!:${}()|[\]\/\\])/g,
          '\\$&'
        )
      );

    const searchValue =
      new RegExp(
        searchValues.join('|'),
        'gi'
      );

    const wrapValue =
      stringValue.replace(
        searchValue,
        `${this.UNIQUE_WRAPPERS[0]}` +
        `$&` +
        `${this.UNIQUE_WRAPPERS[1]}`
      );

    return encodeEntities(wrapValue)
      .replace(
        new RegExp(
          this.UNIQUE_WRAPPERS[0],
          'gi'
        ),
        '<span class="mydocma-tree-search-highlight">'
      )
      .replace(
        new RegExp(
          this.UNIQUE_WRAPPERS[1],
          'gi'
        ),
        '</span>'
      );
  }

  public renderFilterInnerHtml(
    state: StructureFilterDisplayState
  ): string {

    const {
      structure,
      filterTerms,
      matchedFieldGroups
    } = state;

    /*
     * Structure highlighting does not depend
     * on the UI language.
     */
    const structureHtml =
      this.renderStructureName(
        structure,
        filterTerms
      );

    /*
     * Match only in Structure.
     */
    if (matchedFieldGroups.length === 0) {
      return structureHtml;
    }

    /*
     * Exactly one AddressType + field.
     */
    if (matchedFieldGroups.length === 1) {

      const group =
        matchedFieldGroups[0];

      const valuesHtml =
        group.values
          .map(value =>
            this.highlightValues(
              value,
              filterTerms
            )
          )
          .join(', ');

      const addressType =
        this.getLocalizedAddressTypeName(
          group.addressType
        );

      return (
        `${structureHtml} (` +
        `${encodeEntities(addressType)}: ` +
        `${valuesHtml}` +
        `)`
      );
    }

    /*
     * Multiple AddressType + field results.
     *
     * As requested, the result summary itself
     * is NOT highlighted.
     */
    const addressTypes = [
      ...new Set(
        matchedFieldGroups.map(
          group => group.addressType
        )
      )
    ];

    const addressTypesHtml =
      addressTypes
        .map(addressType =>
          encodeEntities(
            this.getLocalizedAddressTypeName(
              addressType
            )
          )
        )
        .join(', ');

    return (
      `${structureHtml} (` +
      `${matchedFieldGroups.length} results in ` +
      `${addressTypesHtml}` +
      `)`
    );
  }

  private getLocalizedAddressTypeName(
    addressType: AddressTypeEnum
  ): string {

    const enumName =
      AddressTypeEnum[addressType];

    return this.translateService.instant(
      `MM.structure.AddressType.${enumName}`
    );
  }

  private isIdField(field: string): boolean {
    return field === 'Id' || field === 'Uid';
  }

}
